import { createAsyncThunk } from "@reduxjs/toolkit";

import { deleteInstrumentDocumentAssociation, deleteInstrumentListingContactRelation, deleteInstrumentPricingRelation, getAllInstrumentsForBulkChange, getAllStaged, getDocumentsByApplicationId, getInstrumentById, getInstrumentListingContactByIssuer, getInstrumentPaginatedData, getInstrumentSearch, getInstrumentStageNSM, getInstrumentsByToken, getPricingAgentsByIssuer, getStageInstrumentData, postInstrumentDocumentAssociation, postInstrumentListingContact, postInstrumentPricingContact, postInstrumentStage, postInstrumentsForBulkChange, postUpdateInstrumentListingContact, postUpdateInstrumentPricingContact, putInstrumentStage , patchInstrumentStageStatus, postWithdrawInstrumentStage, getInstrumentsByDocId, deleteDocumentForNsm, postDocumentForNsm, getListingStats, postInstrumentStageEffectiveDate, getSecurityLookUp, postInstrumentStageLock} from "./cruds";

export const ACTION_TYPE_PARENT_STAGE = 'InstrumentStage';
export const ACTION_TYPE_PARENT = 'Instrument';

//* returns the list of documents associated with the stage application
export const fetchAttachedDocumentsByApplicationId = createAsyncThunk(`${ACTION_TYPE_PARENT_STAGE}/fetchAttachedDocumentsByApplicationId`,
  async ({ nsm, applicationId }: any, { rejectWithValue }) => {
    try {
      const response = await getDocumentsByApplicationId(nsm, applicationId)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

//* created the stage application -> used while saving an application
export const createInstrumentStage = createAsyncThunk(`${ACTION_TYPE_PARENT_STAGE}/createInstrumentStage`,
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await postInstrumentStage(payload)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.response.data?.title ?? err.response.data, errors: err.response.data?.errors })
    }
  }
)

//* updates instrument stage
export const updateInstrumentStage = createAsyncThunk(`${ACTION_TYPE_PARENT}/updateInstrumentStage`,
  async (payload : any, { rejectWithValue }) => {
    try {
      const response = await putInstrumentStage(payload)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.response.data?.title ?? err.response.data, errors : err.response.data?.errors })
    }
  }
)

//* patch update status of instrument stage
export const updateInstrumentStageStatus = createAsyncThunk(`${ACTION_TYPE_PARENT}/updateInstrumentStageStatus`,
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await patchInstrumentStageStatus(payload)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      const {data} = err.response
      let message = ''
      if(Array.isArray(data)) message = data.reduce((acc, curr) => acc + curr.message,'')
      else message = data?.title ?? data
      return rejectWithValue({ status: err.response.status, code: err.code, message, errors : err.response.data?.errors })
    }
  }
)

//* returns all staged instruments
export const fetchAllStagedInstruments = createAsyncThunk(`${ACTION_TYPE_PARENT_STAGE}/fetchAllStagedInstruments`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllStaged()
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

//* returns stage instrument by nsm
export const fetchInstrumentStageByNSM = createAsyncThunk(`${ACTION_TYPE_PARENT_STAGE}/fetchInstrumentStageByNSM`,
  async (nsm: string, { rejectWithValue }) => {
    try {
      const response = await getInstrumentStageNSM(nsm)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

//* creates instrument document association
export const createInstrumentDocumentAssociation = createAsyncThunk(`${ACTION_TYPE_PARENT_STAGE}/createInstrumentDocumentAssociation`,
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await postInstrumentDocumentAssociation(payload)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

//* removes instrument document association
export const removeInstrumentDocumentAssociation = createAsyncThunk(`${ACTION_TYPE_PARENT_STAGE}/removeInstrumentDocumentAssociation`,
  async (instrumentDocumentId: string, { rejectWithValue }) => {
    try {
      const response = await deleteInstrumentDocumentAssociation(instrumentDocumentId)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

//* creates instrument listing contact
export const createInstrumentListingContact = createAsyncThunk(`${ACTION_TYPE_PARENT}/createInstrumentListingContact`,
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await postInstrumentListingContact(payload)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message, data: err.response.data })
    }
  }
)

//* updates instrument listing contact
export const updateInstrumentListingContact = createAsyncThunk(`${ACTION_TYPE_PARENT}/updateInstrumentListingContact`,
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await postUpdateInstrumentListingContact(payload)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

//* creates instrument pricing contact
export const createInstrumentPricingContact = createAsyncThunk(`${ACTION_TYPE_PARENT}/createInstrumentPricingContact`,
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await postInstrumentPricingContact(payload)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message, data: err.response.data })
    }
  }
)

//* updates instrument pricing contact
export const updateInstrumenPricingContact = createAsyncThunk(`${ACTION_TYPE_PARENT}/updateInstrumenPricingContact`,
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await postUpdateInstrumentPricingContact(payload)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message, data: err?.response?.data })
    }
  }
)

//* updates instrument pricing contact
export const removeInstrumentPricingRelation = createAsyncThunk(`${ACTION_TYPE_PARENT}/removeInstrumentPricingRelation`,
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await deleteInstrumentPricingRelation(payload)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err?.response?.data, data: err?.response?.data })
    }
  }
)

//* returns instrument paginated data
export const fetchInstrumentData = createAsyncThunk(`${ACTION_TYPE_PARENT}/fetchInstrumentData`,
  async ({ pageIndex, pageSize, payload, params, requestHeaders = undefined }: any, { rejectWithValue }) => {
    try {
      const response = await getInstrumentPaginatedData(pageIndex, pageSize, payload, params, requestHeaders)
      return response;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.response.data?.title, data : err.response.data })
    }
  }
)

//* returns all staged instruments
export const fetchStageInstrumentData = createAsyncThunk(`${ACTION_TYPE_PARENT}/fetchStageInstrumentData`,
  async ({ payload, params, requestHeaders = undefined }: any, { rejectWithValue }) => {
    try {
      const response = await getStageInstrumentData(payload, params, requestHeaders)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.response.data?.title ?? err.response.data, errors : err.response.data?.errors })
    }
  }
)

//* sets the selected instrument in redux state
export const setSelectedInstrument = createAsyncThunk(`${ACTION_TYPE_PARENT}/setSelectedInstrument`,
  async (payload: any, { rejectWithValue }) => {
    try {
      return payload;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

//* clears the selected instrument from redux state
export const clearSelectedInstrument = createAsyncThunk(`${ACTION_TYPE_PARENT}/clearSelectedInstrument`,
  async () => {
    return [];
  }
)

//* search instrument by symbol, nsm, cusip
export const fetchSearchInstruments = createAsyncThunk(`${ACTION_TYPE_PARENT}/fetchSearchInstruments`,
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await getInstrumentSearch(payload)
      return response.data
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

//* search instruments by NSM, Symbol, CUSIP, ISIN
export const searchInstrumentsByToken = createAsyncThunk(`${ACTION_TYPE_PARENT}/searchInstrumentsByToken`,
  async ({ token, pageNumber, requestHeaders, params }: any, { rejectWithValue }) => {
    try {
      const response = await getInstrumentsByToken(token, pageNumber, requestHeaders, params)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.response.data })
    }
  }
)


//* get instrument by id
export const fetchInstrumentObjectById = createAsyncThunk(`${ACTION_TYPE_PARENT}/fetchInstrumentObjectById`,
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await getInstrumentById(id)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.response.data, errors : err.response.data?.errors })
    }
  }
)

export const fetchPricingAgentsByIssuer = createAsyncThunk(`${ACTION_TYPE_PARENT}/fetchPricingAgentsByIssuer`,
  async ({ issuerID, current, nsm, requestHeaders }: { issuerID: number, current: boolean, nsm: string, requestHeaders ?: any }, { rejectWithValue }) => {
    try {
      const response = await getPricingAgentsByIssuer(issuerID, current, nsm, requestHeaders)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.response.data ?? '' })
    }
  }
)

export const fetchInstrumentListingContactByIssuer = createAsyncThunk(`${ACTION_TYPE_PARENT}/fetchInstrumentListingContactByIssuer`,
  async ({ issuerID, current, nsm }: { issuerID: number, current: boolean, nsm: string }, { rejectWithValue }) => {
    try {
      const response = await getInstrumentListingContactByIssuer(issuerID, current, nsm)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.response.data ?? '' })
    }
  }
)

export const removeInstrumentListingContactRelation = createAsyncThunk(`${ACTION_TYPE_PARENT}/removeInstrumentListingContactRelation`,
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await deleteInstrumentListingContactRelation(payload)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err?.response?.data ?? '' })
    }
  }
)

export const fetchAllInstrumentsForBulkChange = createAsyncThunk(`${ACTION_TYPE_PARENT}/fetchAllInstrumentsForBulkChange`,
  async ({issuerId, requestHeaders}:{issuerId: number, requestHeaders ?: any}, { rejectWithValue }) => {
    try {
      const response = await getAllInstrumentsForBulkChange(issuerId, requestHeaders)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.response.data })
    }
  }
)

export const saveInstrumentsForBulkChange = createAsyncThunk(`${ACTION_TYPE_PARENT}/saveInstrumentsForBulkChange`,
  async (payload : any, { rejectWithValue }) => {
    try {
      const response = await postInstrumentsForBulkChange(payload)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.response.data })
    }
  }
)

//* withdraw instrument stage by nsm
export const withdrawInstrumentStageByNSM = createAsyncThunk(`${ACTION_TYPE_PARENT}/withdrawInstrumentStageByNSM`,
  async (nsm: string, { rejectWithValue }) => {
    try {
      const response = await postWithdrawInstrumentStage(nsm)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.response.data?.title, errors : err.response.data?.errors })
    }
  }
)

//* get list of assigned / unassigned instruments by document ID
export const fetchInstrumentsByDocId = createAsyncThunk(`${ACTION_TYPE_PARENT}/fetchInstrumentsByDocId`,
  async ({documentId, assigned, requestHeaders} : any, { rejectWithValue }) => {
    try {
      const response = await getInstrumentsByDocId(documentId, assigned, requestHeaders)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.response.data?.title, errors : err.response.data?.errors })
    }
  }
)

//* create document association with instrument
export const postDocumentAssociationForInstrument = createAsyncThunk(`${ACTION_TYPE_PARENT}/postDocumentAssociationForInstrument`,
  async ({documentId, nsm, applicationId} : any, { rejectWithValue }) => {
    try {
      const response = await postDocumentForNsm(documentId, nsm, applicationId)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.response.data?.title ?? err.response.data, errors : err.response.data?.errors })
    }
  }
)

//* delete document association with instrument
export const deleteDocumentAssociationForInstrument = createAsyncThunk(`${ACTION_TYPE_PARENT}/deleteDocumentAssociationForInstrument`,
  async ({documentId, nsm, applicationId} : any, { rejectWithValue }) => {
    try {
      const response = await deleteDocumentForNsm(documentId, nsm, applicationId)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.response.data?.title ?? err.response.data, errors : err.response.data?.errors })
    }
  }
)


//* returns all staged instruments
export const fetchListingStats = createAsyncThunk(`${ACTION_TYPE_PARENT_STAGE}/fetchListingStats`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await getListingStats()
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

//* updates instrument stage effective date - Market Close Calendar
export const updateInstrumentStageEffectiveDate = createAsyncThunk(`${ACTION_TYPE_PARENT_STAGE}/updateInstrumentStageEffectiveDate`,
  async ({effectiveDate, instrumentStageIds} : any, { rejectWithValue }) => {
    try {
      const response = await postInstrumentStageEffectiveDate(effectiveDate, instrumentStageIds)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.response.data?.title ?? err.response.data, errors : err.response.data?.errors })
    }
  }
)

export const fetchSecurityLookUp = createAsyncThunk(`${ACTION_TYPE_PARENT_STAGE}/fetchSecurityLookUp`,
  async ({ payload, requestHeaders }: any, { rejectWithValue }) => {
    try {
      const response = await getSecurityLookUp(payload, requestHeaders)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.response.data, errors: err.response.data?.errors })
    }
  }
)

//* returns the instrument stage lock status or creates one if unavailable 
export const fetchInstrumentStageLock = createAsyncThunk(`${ACTION_TYPE_PARENT_STAGE}/fetchInstrumentStageLock`,
  async ({ nsm }: {nsm: string}, { rejectWithValue }) => {
    try {
      const response = await postInstrumentStageLock(nsm)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.response.data, errors: err.response.data?.errors })
    }
  }
)